import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import LayersIcon from '@material-ui/icons/Layers';
import {
  createFrontendModule,
  iconsApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import {
  NavContentBlueprint,
  NavContentComponentProps,
} from '@backstage/plugin-app-react';
import { compatWrapper } from '@backstage/core-compat-api';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

/**
 * Page extensions that are deliberately not sidebar entries.
 *
 * Every page extension with a resolvable, parameterless route ref becomes a nav
 * item, and `navItems.rest()` renders whatever is left over. These five are
 * reached from elsewhere in the UI — search from the search group, settings from
 * the settings group at the bottom, and the other three from within the catalog
 * — so they are taken out of `rest()` without being rendered.
 */
const NOT_IN_SIDEBAR = [
  'page:search',
  'page:user-settings',
  'page:catalog-import',
  'page:catalog-graph',
];

/**
 * Page extensions that are rendered by hand further down, either because they
 * carry a submenu or because they use an icon of their own.
 */
const PLACED_BY_HAND = [
  'page:explore',
  'page:catalog',
  'page:api-docs',
  'page:techdocs',
  'page:scaffolder',
];

const SidebarContent = ({ navItems }: NavContentComponentProps) => {
  const icons = useApi(iconsApiRef);
  const nav = navItems.withComponent(item => (
    <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
  ));

  // Taking has to happen before `rest()` is rendered.
  for (const id of [...NOT_IN_SIDEBAR, ...PLACED_BY_HAND]) {
    nav.take(id);
  }

  // The sidebar is built from old frontend system components throughout
  // (`SidebarSearchModal`, `MyGroupsSidebarItem`, `SidebarSettings`), so it
  // needs the backwards compatibility context.
  return compatWrapper(
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        <SidebarItem icon={LayersIcon} to="explore" text="Explore" />
        <SidebarItem icon={HomeIcon} to="catalog" text="Home">
          <SidebarSubmenu title="Catalog">
            <SidebarSubmenuItem
              title="Domains"
              to="catalog?filters[kind]=domain"
              icon={icons.getIcon('kind:domain')}
            />
            <SidebarSubmenuItem
              title="Systems"
              to="catalog?filters[kind]=system"
              icon={icons.getIcon('kind:system')}
            />
            <SidebarSubmenuItem
              title="Components"
              to="catalog?filters[kind]=component"
              icon={icons.getIcon('kind:component')}
            />
            <SidebarSubmenuItem
              title="APIs"
              to="catalog?filters[kind]=api"
              icon={icons.getIcon('kind:api')}
            />
            <SidebarDivider />
            <SidebarSubmenuItem
              title="Resources"
              to="catalog?filters[kind]=resource"
              icon={icons.getIcon('kind:resource')}
            />
            <SidebarDivider />
            <SidebarSubmenuItem
              title="Groups"
              to="catalog?filters[kind]=group"
              icon={icons.getIcon('kind:group')}
            />
            <SidebarSubmenuItem
              title="Users"
              to="catalog?filters[kind]=user"
              icon={icons.getIcon('kind:user')}
            />
          </SidebarSubmenu>
        </SidebarItem>
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper>
          {/*
            The portal's own plugins (Client Secrets, Maintenance)
            land here, each with the title and icon its plugin declares, as does
            any plugin added later. Items in this group scroll if they run out
            of space.
          */}
          {nav.rest({ sortBy: 'title' })}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>,
  );
};

export const navModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    NavContentBlueprint.make({ params: { component: SidebarContent } }),
  ],
});
